import './KeywordSearchTrends.scss';
import * as Dfns from 'date-fns';
import * as R from 'ramda';
import { ReactElement, useCallback, useMemo, useState } from 'react';
import { AutoSizer } from 'react-virtualized-auto-sizer';
import { CartesianGrid, Line, LineChart as LC, Tooltip, XAxis, YAxis } from 'recharts';
import {
  KeywordTrendEntry,
  KeywordTrendMetrics
} from '@blisspointmedia/bpm-types/dist/CommerceOps';
import { IMP_FORMATTER } from '@blisspointmedia/bpm-types/dist/metricFormatUtils';
import {
  DATE_GROUPING_TYPES,
  DateGroupingType
} from '@blisspointmedia/bpm-types/dist/MetricsTable';

import { Size } from '@Components/designConstants';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { DownloadDropdown } from '@Components/DownloadDropdown/DownloadDropdown';
import { Dropdown, DropdownToggleType } from '@Components/Dropdown';
import { FilterBar } from '@Components/FilterBar/FilterBar';
import { GraphTableToggleButton } from '@Components/GraphTableToggleButton/GraphTableToggleButton';
import { ChartContainer } from '@Components/ChartContainer';
import {
  CARTESIAN_GRID_STROKE,
  CARTESIAN_GRID_STROKE_WIDTH,
  TICK_STYLE
} from '@Components/Charts/ChartConstants';
import { ChartTooltip } from '@Components/Charts/ChartTooltip/ChartTooltip';
import {
  buildEquidistantScale,
  buildTimeScale,
  useDynamicYAxisWidth
} from '@Components/Charts/ChartUtils';
import { getColorMap, Neutral500 } from '@src/utils/colors';
import { downloadPNG } from '@src/utils/download-utils';
import { CommerceOpsTable } from './CommerceOpsTable';
import {
  DATE_GROUPING_TYPES_TO_PRETTY_NAME,
  KEYWORD_TREND_DIMENSION_CONFIG_MAP,
  KeywordTrendDescriptiveField,
  KeywordTrendDimension
} from './CommerceOpsUtils';

interface KeywordSearchTrendsProps {
  data: Record<DateGroupingType, KeywordTrendEntry<KeywordTrendMetrics>[]>;
  downloadTable: (fileNameSubject: string, dataMap: any) => void;
}

const DATE_GROUPING_OPTIONS = DATE_GROUPING_TYPES.map(type => ({
  value: type,
  label: DATE_GROUPING_TYPES_TO_PRETTY_NAME[type]
}));

const TICK_COUNT = 5;
const X_AXIS_HEIGHT_MODIFIER = 5;
const Y_AXIS_WIDTH_MODIFIER = 6;

const KeywordSearchTrendsTooltipContent = ({
  active,
  label,
  payload
}: any): ReactElement | null => {
  if (active && payload && payload.length) {
    const items =
      R.sortBy((item: any) => -Math.abs(item.value), payload).map(item => ({
        label: item.name as string,
        value: IMP_FORMATTER(item.value as number),
        color: item.stroke
      })) || [];

    return (
      <ChartTooltip
        items={items}
        headerLabel={`${Dfns.format(label as number, 'M/dd/yy')} Impressions`}
        shape='line'
      />
    );
  }
  return null;
};

const trendFilterOptions = [
  { name: KeywordTrendDescriptiveField.DATE, label: 'Date' },
  { name: KeywordTrendDescriptiveField.KEYWORD, label: 'Keyword' }
];

const KeywordSearchTrends = ({ data, downloadTable }: KeywordSearchTrendsProps): ReactElement => {
  const [trendDateGrouping, setTrendDateGrouping] = useState<DateGroupingType>(
    DATE_GROUPING_OPTIONS[0].value
  );
  const [graphView, setGraphView] = useState(true);

  const [trendFilterObj, setTrendFilterObj] = useState<{
    filterFn: (line: Record<string, any>) => boolean;
  }>();

  const [focusedKeyword, setFocusedKeyword] = useState<string>('');
  const [hoveredKeyword, setHoveredKeyword] = useState<string>('');

  const trendTableDataMap = useMemo(
    () => ({ [KeywordTrendDimension.KEYWORD]: data[trendDateGrouping] }),
    [trendDateGrouping, data]
  );

  const allKeywords: string[] = useMemo(() => {
    const keywordSet = new Set<string>();
    trendTableDataMap[KeywordTrendDimension.KEYWORD].forEach(entry =>
      keywordSet.add(entry.keyword)
    );
    return Array.from(keywordSet);
  }, [trendTableDataMap]);

  const downloadTrendCsv = useCallback(
    () => downloadTable('trends', trendTableDataMap),
    [trendTableDataMap, downloadTable]
  );

  const downloadTrendPng = useCallback(async () => {
    await downloadPNG(
      '.commerce-ops-section .chart-container .contents',
      `${DATE_GROUPING_TYPES_TO_PRETTY_NAME[trendDateGrouping].toLocaleLowerCase()}_keyword_trends`
    );
  }, [trendDateGrouping]);

  const trendFilterLines = useMemo(
    (): object[] => data[trendDateGrouping],
    [data, trendDateGrouping]
  );

  const { filteredTableData, filteredChartData, filteredKeywords } = useMemo(() => {
    const tableData = trendTableDataMap[KeywordTrendDimension.KEYWORD].filter(
      row => trendFilterObj?.filterFn(row) ?? true
    );

    const chartData: Record<string, number>[] = Object.entries(
      R.groupBy(row => row.date, tableData)
    )
      .map(([date, trendEntries]) => ({
        time: Dfns.parseISO(date).getTime(),
        ...Object.fromEntries(trendEntries.map(entry => [entry.keyword, entry.metrics.impressions]))
      }))
      .sort((a, b) => a.time - b.time);

    const keywords = Array.from(new Set(tableData.map(entry => entry.keyword)));

    return {
      filteredTableData: tableData,
      filteredChartData: chartData,
      filteredKeywords: keywords
    };
  }, [trendFilterObj, trendTableDataMap]);

  const [trendXScale, trendYScale] = useMemo(() => {
    const uniqueTimes = filteredChartData.map(entry => entry.time);
    const yMax = Math.max(
      ...filteredChartData.map(entry => {
        const { time, ...rest } = entry;
        return Math.max(...Object.values(rest));
      })
    );

    return [
      buildTimeScale(uniqueTimes[0], uniqueTimes[uniqueTimes.length - 1], uniqueTimes, TICK_COUNT),
      // always start impression scale at 0
      buildEquidistantScale(0, yMax, TICK_COUNT)
    ];
  }, [filteredChartData]);

  const colorMap = useMemo(
    () =>
      R.isEmpty(allKeywords)
        ? undefined
        : getColorMap(allKeywords.map(keyword => ({ name: keyword }))),
    [allKeywords]
  );

  const { xAxisHeight, yAxisWidth, setChartRef } = useDynamicYAxisWidth({
    xAxisHeightModifier: x => x + X_AXIS_HEIGHT_MODIFIER,
    yAxisWidthModifier: x => x + Y_AXIS_WIDTH_MODIFIER
  });

  const resolveLineColor = useCallback(
    (keyword: string): string => {
      const isFocused = focusedKeyword === '' || focusedKeyword === keyword;
      const isHovered = hoveredKeyword === '' || hoveredKeyword === keyword;
      const focusColor = isFocused ? colorMap[keyword] : Neutral500;
      const hoverColor = isHovered ? colorMap[keyword] : Neutral500;
      return focusedKeyword ? focusColor : hoverColor;
    },
    [focusedKeyword, hoveredKeyword, colorMap]
  );

  const title = useMemo(
    () => (
      <>
        <Dropdown
          type={DropdownToggleType.WIDGET_TITLE}
          value={trendDateGrouping}
          options={DATE_GROUPING_OPTIONS}
          onChange={setTrendDateGrouping}
        />
        Search Trend of Top Keywords
      </>
    ),
    [trendDateGrouping]
  );

  const rightActions = useMemo(
    () => (
      <div className='commerce-ops-header-controls'>
        <FilterBar
          size={Size.LG}
          onFilter={filter => setTrendFilterObj({ filterFn: filter })}
          lines={trendFilterLines}
          options={trendFilterOptions}
          storageKeySuffix='commerce-ops-keyword-trends'
        />
        <GraphTableToggleButton
          graphView={graphView}
          setGraphView={setGraphView}
          widgetName='keyword_search_trends'
        />
        <DownloadDropdown size={Size.LG} onClickOptions={[downloadTrendCsv, downloadTrendPng]} />
      </div>
    ),
    [graphView, trendFilterLines, downloadTrendCsv, downloadTrendPng]
  );

  const centralChartContent = useMemo(() => {
    if (R.isNil(filteredChartData) || R.isNil(filteredTableData)) {
      return null;
    }

    if (!graphView) {
      return (
        <div className='trend-content trend-table'>
          <CommerceOpsTable
            topRowConfig={{ showTopRow: false }}
            dimensionConfigMap={KEYWORD_TREND_DIMENSION_CONFIG_MAP}
            dataMap={{ [KeywordTrendDimension.KEYWORD]: filteredTableData }}
          />
        </div>
      );
    }

    return (
      <div className='trend-content'>
        <div className='trend-chart-wrapper'>
          <div className='y-axis-title'>Impressions</div>
          <div className='trend-chart'>
            <AutoSizer
              renderProp={({ width = 0, height = 0 }) => (
                <LC width={width} height={height} ref={setChartRef} data={filteredChartData}>
                  <CartesianGrid
                    stroke={CARTESIAN_GRID_STROKE}
                    strokeWidth={CARTESIAN_GRID_STROKE_WIDTH}
                    vertical={false}
                  />
                  <XAxis
                    axisLine={false}
                    dataKey='time'
                    type='number'
                    tick={TICK_STYLE}
                    tickFormatter={time => Dfns.format(time, 'M/dd/yy')}
                    height={xAxisHeight}
                    domain={trendXScale.domain}
                    ticks={trendXScale.ticks}
                  />
                  <YAxis
                    axisLine={false}
                    type='number'
                    tick={TICK_STYLE}
                    tickFormatter={imps => IMP_FORMATTER(imps)}
                    tickLine={false}
                    width={yAxisWidth}
                    domain={trendYScale.domain}
                    ticks={trendYScale.ticks}
                  />
                  <Tooltip
                    content={<KeywordSearchTrendsTooltipContent />}
                    isAnimationActive={false}
                  />
                  {filteredKeywords.map(keyword => {
                    if (focusedKeyword !== '' && focusedKeyword !== keyword) {
                      return null;
                    }
                    return (
                      <Line
                        dataKey={keyword}
                        isAnimationActive={false}
                        key={keyword}
                        name={keyword}
                        stroke={resolveLineColor(keyword)}
                        strokeWidth={3}
                        dot={false}
                        type='monotone'
                      />
                    );
                  })}
                </LC>
              )}
            />
          </div>
        </div>
        <div className='right-of-chart'>
          <div className='legend'>
            {filteredKeywords.map(keyword => (
              <InteractiveDiv
                className='legend-item'
                key={keyword}
                onClick={() => {
                  setFocusedKeyword(prev => (prev === keyword ? '' : keyword));
                  setHoveredKeyword('');
                }}
                onMouseEnter={() => {
                  if (focusedKeyword === '') {
                    setHoveredKeyword(keyword);
                  }
                }}
                onMouseLeave={() => {
                  if (focusedKeyword === '') {
                    setHoveredKeyword('');
                  }
                }}
              >
                <div
                  className='line'
                  style={{
                    backgroundColor: resolveLineColor(keyword)
                  }}
                />
                <div>{keyword}</div>
              </InteractiveDiv>
            ))}
          </div>
        </div>
      </div>
    );
  }, [
    filteredKeywords,
    filteredTableData,
    filteredChartData,
    graphView,
    setChartRef,
    xAxisHeight,
    trendXScale,
    yAxisWidth,
    trendYScale,
    focusedKeyword,
    resolveLineColor
  ]);

  return (
    <ChartContainer title={title} rightActions={rightActions} viewRightActions>
      {centralChartContent}
    </ChartContainer>
  );
};

export { KeywordSearchTrends };
